"use client";

import { InfoIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

// UX-A (Andrei, 2026-10-26: "don't use explainer long phrases.. use icons (i) with popovers"):
// the one place an explanation lives. Labels stay short; what they mean sits behind a small (i)
// beside them, opened by hover, focus, click or tap. `InfoHelp` is the page header's larger "?".

export type InfoTipProps = {
  /** What the popover says: one or two short sentences. */
  children: React.ReactNode;
  /** Accessible name of the trigger, e.g. "About Published". */
  label?: string;
  className?: string;
};

/** A 20 px (i) button; the explanation opens in a popover at most 280 px wide. */
export function InfoTip({ children, label = "More about this", className }: InfoTipProps) {
  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={150}
        aria-label={label}
        data-slot="info-tip"
        className={cn(
          "inline-flex size-5 shrink-0 items-center justify-center rounded-sm align-middle text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring pointer-coarse:size-8",
          className,
        )}
      >
        <InfoIcon aria-hidden="true" className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-auto max-w-[280px] p-3 text-[13px] leading-[1.55] text-secondary"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
